import { z } from 'zod';
import {
  DEFAULT_CURRENCY,
  ORDER_TYPE_LABELS,
  DAYS_LIST,
  moneySchema,
  availabilityRuleSchema,
  type TAvailabilityRule,
} from '../../schemas';
import type { TMenuCategory } from '../../types';

export { DEFAULT_CURRENCY, ORDER_TYPE_LABELS, DAYS_LIST };
export type { TMenuCategory, TAvailabilityRule };

// coupon-specific tier shape (different from discount tiers)
const tierSchema = z.object({
  minOrderValue: z.object({
    amount: z.number(),
    currency: z.string(),
  }),
  discount: z.object({
    type: z.enum(['percentage', 'fixed']),
    value: z.number(),
    currency: z.string().optional(),
  }),
});

// --- API response schema ---

export const couponSchema = z.object({
  _id: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  discountType: z.enum(['percentage', 'fixed']),
  discountPercentage: z.number().optional(),
  discountAmount: moneySchema.optional().nullable(),
  maxDiscount: moneySchema.optional().nullable(),
  minPurchase: moneySchema.optional().nullable(),
  usageLimit: z.number().optional().nullable(),
  usageCount: z.number().default(0),
  userUsageLimit: z.number().optional().nullable(),
  applicableOrderType: z.array(z.number()),
  _idCategories: z.array(z.string()).default([]),
  status: z.enum(['active', 'disabled']),
  validFrom: z.string().optional().nullable(),
  validTo: z.string().optional().nullable(),
  tiers: z.array(tierSchema).default([]),
  availabilityRules: z.array(availabilityRuleSchema).default([]),
  bannerImage: z.string().nullable().optional(),
  _idRestaurant: z.string(),
  type: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  __v: z.number().optional(),
});

// --- Form schema (create / edit) ---

export const couponFormSchema = z
  .object({
    code: z
      .string()
      .min(1, 'Coupon code is required')
      .max(20, 'Code cannot exceed 20 characters')
      .regex(/^[A-Z0-9_-]+$/i, 'Code can only contain letters, numbers, hyphens, and underscores')
      .transform((val) => val.trim().toUpperCase()),
    description: z.string().optional(),
    status: z.enum(['active', 'disabled']).default('active'),
    discountType: z.enum(['percentage', 'fixed']),
    currency: z.string().default(DEFAULT_CURRENCY),
    _idCategories: z.array(z.string()).default([]),
    discountPercentage: z
      .number({ invalid_type_error: 'Enter a valid number' })
      .nullable()
      .optional(),
    discountAmount: z
      .object({
        value: z
          .number({ invalid_type_error: 'Enter a valid amount' })
          .nullable()
          .optional(),
        currency: z.string().default(DEFAULT_CURRENCY),
      })
      .nullable()
      .optional(),
    maxDiscount: z
      .object({
        value: z.number({ invalid_type_error: 'Enter a valid amount' }).nullable().optional(),
        currency: z.string().default(DEFAULT_CURRENCY),
      })
      .nullable()
      .optional(),
    minPurchase: z
      .object({
        value: z.number({ invalid_type_error: 'Enter a valid amount' }).nullable().optional(),
        currency: z.string().default(DEFAULT_CURRENCY),
      })
      .nullable()
      .optional(),
    usageLimit: z.number().nullable().optional(),
    userUsageLimit: z.number().nullable().optional(),
    validFrom: z.string().nullable().optional(),
    validTo: z.string().nullable().optional(),
    applicableOrderType: z.array(z.number()).min(1, 'Select at least one order type'),
    tiers: z
      .array(
        z.object({
          minOrderValue: z.object({
            amount: z.number({ invalid_type_error: 'Enter a valid amount' }).min(0),
            currency: z.string().default(DEFAULT_CURRENCY),
          }),
          discount: z.object({
            type: z.enum(['percentage', 'fixed']),
            value: z
              .number({ invalid_type_error: 'Enter a valid number' })
              .min(0.01, 'Must be greater than 0'),
            currency: z.string().optional(),
          }),
        })
      )
      .default([]),
    availabilityRules: z
      .array(
        z.object({
          days: z.array(z.string()).min(1, 'Select at least one day'),
          from: z.string().min(1, 'Start time is required'),
          to: z.string().min(1, 'End time is required'),
        })
      )
      .default([]),
    bannerImage: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.discountType === 'percentage') {
      if (!data.discountPercentage || data.discountPercentage <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Discount percentage is required and must be greater than 0',
          path: ['discountPercentage'],
        });
      } else if (data.discountPercentage > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Percentage cannot exceed 100',
          path: ['discountPercentage'],
        });
      }
    }

    if (data.discountType === 'fixed') {
      if (!data.discountAmount?.value || data.discountAmount.value <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Discount amount is required and must be greater than 0',
          path: ['discountAmount', 'value'],
        });
      }
    }

    if (data.validFrom && data.validTo && new Date(data.validFrom) >= new Date(data.validTo)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
        path: ['validTo'],
      });
    }

    data.tiers.forEach((tier, i) => {
      if (tier.discount.type === 'percentage' && tier.discount.value > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Percentage cannot exceed 100',
          path: ['tiers', i, 'discount', 'value'],
        });
      }
    });
  });

// --- Types ---

export type TCoupon = z.infer<typeof couponSchema>;
export type TCouponForm = z.infer<typeof couponFormSchema>;

export type TCouponsResponse = {
  success: boolean;
  code: number;
  msg: string;
  serverTime: string;
  data: {
    rows: TCoupon[];
    total: number;
    pageNum: number;
    pageSize: number;
    pages: number;
  };
};

export type TCouponResponse = {
  success: boolean;
  data: TCoupon;
};
